import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { MyLeads } from "@/components/MyLeads";
import { useAuth } from "@/context/AuthContext";
import { IndianRupee, Wallet, TrendingUp, AlertCircle, Lock, MapPin, Camera, ClipboardList, CalendarClock } from "lucide-react";

// iter163 — Read-only "My Earnings" view for the currently logged-in
// IN-HOUSE staff member. The backend endpoint `/team/me/earnings` resolves
// the staff record from `req.user.id` (not a query param), so this page
// can NEVER be pointed at another staff member's data.
//
// Strictly read-only:
//   • No Edit / Delete / Payment / Adjust buttons on the page.
//   • Numbers come from the existing Payroll + Staff Ledger endpoints; no
//     duplicate financial math.

const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-IN",
      { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

function StatCard({ label, value, icon: Icon, tone = "stone", testid }) {
  const tones = {
    stone: "bg-stone-50 border-stone-200 text-stone-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.stone}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-70">
        {Icon ? <Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> : null}
        {label}
      </div>
      <div className="mt-2 text-2xl font-serif" data-testid={testid ? `${testid}-value` : undefined}>
        {value}
      </div>
    </div>
  );
}

export default function MyEarnings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState(""); // "" = all, "YYYY-MM" otherwise
  // iter164 — Upcoming vs Completed filter for the "My Shoots / My Work"
  // section. "all" is the default so nothing is hidden by mistake.
  const [shootStatusFilter, setShootStatusFilter] = useState("all"); // "all" | "upcoming" | "completed"
  // iter198.6 — Payment history (admin-recorded payments + adjustments).
  const [history, setHistory] = useState(null);
  const [historyErr, setHistoryErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [earnings, hist] = await Promise.all([
          api.get("/team/me/earnings"),
          api.get("/team/me/payment-history").catch((e) => { setHistoryErr(e.response?.data?.detail || ""); return { data: { entries: [], count: 0, total_paid: 0, closing_balance: 0 } }; }),
        ]);
        if (alive) {
          setData(earnings.data);
          setHistory(hist.data);
        }
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || e.message || "Failed to load earnings");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const monthOptions = useMemo(() => {
    if (!data) return [];
    const s = new Set();
    for (const sh of data.shoots || []) {
      if (sh.event_date && sh.event_date.length >= 7) s.add(sh.event_date.slice(0, 7));
    }
    for (const w of data.weekly_payments || []) {
      if (w.week_start && w.week_start.length >= 7) s.add(w.week_start.slice(0, 7));
    }
    return Array.from(s).sort().reverse();
  }, [data]);

  const shootRows = useMemo(() => {
    const rows = data?.shoots || [];
    const todayIso = new Date().toISOString().slice(0, 10);
    return rows.filter((r) => {
      if (monthFilter && !(r.event_date || "").startsWith(monthFilter)) return false;
      if (shootStatusFilter === "upcoming") {
        return !r.event_date || r.event_date >= todayIso;
      }
      if (shootStatusFilter === "completed") {
        return !!r.event_date && r.event_date < todayIso;
      }
      return true;
    });
  }, [data, monthFilter, shootStatusFilter]);

  // iter164 — Counts drive the tab pills so operators know at a glance
  // how many upcoming vs completed shoots they have.
  const shootCounts = useMemo(() => {
    const rows = data?.shoots || [];
    const todayIso = new Date().toISOString().slice(0, 10);
    let upcoming = 0, completed = 0;
    for (const r of rows) {
      if (!r.event_date || r.event_date >= todayIso) upcoming += 1;
      else completed += 1;
    }
    return { upcoming, completed, total: rows.length };
  }, [data]);

  // iter166 — Earnings Breakdown for the currently-selected filter.
  // Shoot side reuses the same math as the backend `totals` fields:
  //   • earned  = amount + max(0, adjust) proportion  → for "All time" we
  //     just trust the server totals; for a month we recompute from the
  //     visible shoot rows (bonus/override are lifetime-only and folded
  //     back into the "All time" view to keep parity with combined_lifetime).
  //   • paid    = when payroll_frozen (booking Completed) → amount.
  //   • pending = when not frozen → amount.
  // Payroll side: weekly rows are only recorded once PAID, so
  //   earned == paid, pending == 0 (deferred payroll — iter159 semantics).
  const breakdown = useMemo(() => {
    if (!data) {
      return {
        shoot: { earned: 0, paid: 0, pending: 0 },
        payroll: { earned: 0, paid: 0, pending: 0 },
        combined: 0,
      };
    }
    const t = data.totals || {};
    if (!monthFilter) {
      // All-time uses the server-authoritative totals (includes ledger
      // overrides + bonuses that are lifetime, not month-scoped).
      const shootEarned = Number(t.shoot_earnings || 0);
      const shootPaid = Number(t.shoot_paid || 0);
      const shootPending = Number(t.shoot_outstanding || 0);
      const payrollEarned = Number(t.weekly_paid || 0);
      return {
        shoot: { earned: shootEarned, paid: shootPaid, pending: shootPending },
        payroll: { earned: payrollEarned, paid: payrollEarned, pending: 0 },
        combined: Number(t.combined_lifetime || 0),
      };
    }
    // Month-scoped: derive from the visible rows without inventing a new
    // calculation — sum `amount` on shoots, `amount` on weekly payments.
    // iter177 — Only rows classified as "earned" (payroll_frozen=True)
    // contribute to Shoot Earned/Paid. Rows classified as "scheduled"
    // (upcoming CONFIRM) or "excluded" (CANCEL/WAITING/INQUIRY) do NOT
    // add to Earned — this matches the corrected all-time totals.
    let sEarned = 0, sPaid = 0, sPending = 0;
    for (const r of (data.shoots || [])) {
      if (!(r.event_date || "").startsWith(monthFilter)) continue;
      const state = r.earning_state
        || (r.payroll_frozen ? "earned" : "scheduled");
      if (state !== "earned") continue;
      const amt = Number(r.amount || 0);
      sEarned += amt;
      sPaid += amt;
    }
    let wEarned = 0;
    for (const w of (data.weekly_payments || [])) {
      if (!(w.week_start || "").startsWith(monthFilter)) continue;
      wEarned += Number(w.amount || 0);
    }
    return {
      shoot: { earned: sEarned, paid: sPaid, pending: sPending },
      payroll: { earned: wEarned, paid: wEarned, pending: 0 },
      combined: sEarned + wEarned,
    };
  }, [data, monthFilter]);

  const weeklyRows = useMemo(() => {
    const rows = data?.weekly_payments || [];
    if (!monthFilter) return rows;
    return rows.filter((r) => (r.week_start || "").startsWith(monthFilter));
  }, [data, monthFilter]);

  if (loading) {
    return <div className="p-6 text-stone-500" data-testid="my-earnings-loading">Loading your earnings…</div>;
  }
  if (err) {
    const notLinked = /linked/i.test(err);
    return (
      <div className="p-6" data-testid="my-earnings-error">
        <div className="max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3 text-amber-900">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <div className="font-semibold">Earnings not available</div>
            <div className="text-sm mt-1">{err}</div>
            {/* iter199.1 — When an admin (or any user) hits the unlinked-
                staff 404, point them to the Team page where the staff
                record can be linked to this account. Coordinators should
                ask their admin; the link only shows for admins so a staff
                member can't be sent to a page they can't use. */}
            {notLinked && user?.role === "admin" && (
              <Link
                to="/team"
                className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-amber-900 underline decoration-amber-400 underline-offset-2 hover:text-amber-950"
                data-testid="my-earnings-link-staff-cta"
              >
                Open Team page to link your staff record →
              </Link>
            )}
          </div>
        </div>
        <div className="mt-6"><MyLeads /></div>
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || {};
  return (
    <div className="p-6 space-y-6" data-testid="my-earnings-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-serif text-stone-900" data-testid="my-earnings-name">
            {data.staff?.name || "My Earnings"}
          </h1>
          <p className="text-sm text-stone-500 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Read-only view · Reach out to Admin for corrections
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-stone-500 font-medium">Filter by month</label>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="input h-9 w-40"
            data-testid="my-earnings-month-filter"
          >
            <option value="">All time</option>
            {monthOptions.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          testid="my-earnings-stat-total"
          label="Total Earned (Lifetime)"
          value={fmtInr(t.combined_lifetime)}
          icon={TrendingUp}
        />
        <StatCard
          testid="my-earnings-stat-paid"
          tone="emerald"
          label="Total Paid"
          value={fmtInr(t.total_paid)}
          icon={Wallet}
        />
        {/* iter198.5 — Pending balance card mirrors the Admin Staff Ledger.
            Renders the SIGNED `outstanding_due` verbatim:
              > 0  → amber "Pending"        (money the studio still owes)
              = 0  → stone "Pending" ₹0
              < 0  → rose  "Pending" ₹-X    (staff was overpaid; carry-forward)
            No zero-clamping. `fmtInr` renders the "-" sign as `₹-18,400`. */}
        {(() => {
          const netRaw = t.outstanding_due ?? t.net_balance ?? 0;
          const net = Number(netRaw);
          const tone = net > 0 ? "amber" : net < 0 ? "rose" : "stone";
          const Icon = net < 0 ? AlertCircle : IndianRupee;
          return (
            <StatCard
              testid="my-earnings-stat-pending"
              tone={tone}
              label="Pending"
              value={fmtInr(net)}
              icon={Icon}
            />
          );
        })()}
        <StatCard
          testid="my-earnings-stat-weekly"
          label={`Weekly Payroll (${t.weekly_count || 0})`}
          value={fmtInr(t.weekly_paid)}
          icon={Wallet}
        />
      </section>

      {/* iter177 — Upcoming / Scheduled earnings from CONFIRM bookings
          whose shoots haven't happened yet. Rendered only when > 0 so
          the layout stays identical for staff with no future shoots. */}
      {Number(t.scheduled_earnings || 0) > 0 && (
        <section
          className="rounded-lg border border-sky-200 bg-sky-50/50 px-4 py-3 flex items-center justify-between flex-wrap gap-2"
          data-testid="my-earnings-scheduled-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white border border-sky-200 flex items-center justify-center">
              <CalendarClock className="w-4 h-4 text-sky-700" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-700">
                Upcoming / Scheduled Earnings{t.scheduled_count ? ` · ${t.scheduled_count} shoot${t.scheduled_count === 1 ? "" : "s"}` : ""}
              </div>
              <div className="text-[11px] text-sky-800/70">
                From confirmed future shoots — not yet included in Total Earned.
              </div>
            </div>
          </div>
          <div className="text-2xl font-serif text-sky-900 tabular-nums" data-testid="my-earnings-scheduled-amount">
            {fmtInr(t.scheduled_earnings)}
          </div>
        </section>
      )}

      {/* iter166 — Earnings Breakdown: Shoot Payments vs Daily / Weekly
          Payroll. Purely presentational — same underlying numbers as the
          summary cards above; the two sub-cards must sum to Total Earned. */}
      <section data-testid="earnings-breakdown-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-stone-500">
            Earnings Breakdown
          </h2>
          <div className="text-xs text-stone-500" data-testid="earnings-breakdown-combined">
            Combined <span className="font-mono font-semibold text-stone-800">{fmtInr(breakdown.combined)}</span>
            {monthFilter ? <span className="ml-1 text-[10px]">· {monthFilter}</span> : <span className="ml-1 text-[10px]">· All time</span>}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-rose-200 bg-white p-4" data-testid="breakdown-shoot">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">
                Shoot Payments
              </div>
              <Camera className="w-3.5 h-3.5 text-rose-400" strokeWidth={1.8} />
            </div>
            <div className="mt-1 text-2xl font-serif text-stone-900 tabular-nums" data-testid="breakdown-shoot-earned">
              {fmtInr(breakdown.shoot.earned)}
            </div>
            <div className="text-[11px] text-stone-500">From assigned shoots</div>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded bg-emerald-50 border border-emerald-100 px-2 py-1">
                <dt className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Paid</dt>
                <dd className="font-mono text-emerald-900" data-testid="breakdown-shoot-paid">{fmtInr(breakdown.shoot.paid)}</dd>
              </div>
              <div className="rounded bg-amber-50 border border-amber-100 px-2 py-1">
                <dt className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Pending</dt>
                <dd className="font-mono text-amber-900" data-testid="breakdown-shoot-pending">{fmtInr(breakdown.shoot.pending)}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-lg border border-sky-200 bg-white p-4" data-testid="breakdown-payroll">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-700">
                Daily / Weekly Payroll
              </div>
              <Wallet className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} />
            </div>
            <div className="mt-1 text-2xl font-serif text-stone-900 tabular-nums" data-testid="breakdown-payroll-earned">
              {fmtInr(breakdown.payroll.earned)}
            </div>
            <div className="text-[11px] text-stone-500">From regular payroll</div>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded bg-emerald-50 border border-emerald-100 px-2 py-1">
                <dt className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Paid</dt>
                <dd className="font-mono text-emerald-900" data-testid="breakdown-payroll-paid">{fmtInr(breakdown.payroll.paid)}</dd>
              </div>
              <div className="rounded bg-stone-50 border border-stone-100 px-2 py-1">
                <dt className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Pending</dt>
                <dd className="font-mono text-stone-700" data-testid="breakdown-payroll-pending">{fmtInr(breakdown.payroll.pending)}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* iter198.6 — Payment History (admin-recorded payments).
          Sourced from `/team/me/payment-history` — the same event log
          the Admin Statement uses, so this view and the admin ledger
          can never disagree. Read-only. */}
      {history && (history.entries || []).length > 0 && (
        <section
          className="rounded-lg border border-stone-200 bg-white overflow-hidden"
          data-testid="payment-history-section"
        >
          <header className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-serif text-lg text-stone-800">Payment History</h2>
              <p className="text-[11px] text-stone-500 mt-0.5">
                Every payment your studio has recorded, newest first — the same amounts
                admin sees in the Staff Ledger.
              </p>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                Total received
              </div>
              <div className="font-mono text-lg text-stone-900" data-testid="payment-history-total">
                {fmtInr(history.total_paid || 0)}
              </div>
            </div>
          </header>

          <ol className="divide-y divide-stone-100" data-testid="payment-history-list">
            {history.entries.map((e) => (
              <li
                key={e.id}
                className={`px-4 py-3 flex items-start gap-3 ${e.voided ? "opacity-60" : ""}`}
                data-testid={`payment-history-row-${e.id}`}
              >
                <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  e.voided
                    ? "bg-stone-100 text-stone-400 border border-stone-200"
                    : e.type === "adjustment"
                      ? "bg-amber-50 text-amber-700 border border-amber-200"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                }`}>
                  <Wallet className="w-4 h-4" strokeWidth={1.8} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-stone-900">
                      {e.type === "adjustment"
                        ? (Number(e.amount_signed) >= 0 ? "Bonus / Adjustment" : "Adjustment (deducted)")
                        : "Payment Received"}
                    </div>
                    {e.voided && (
                      <span className="text-[9px] uppercase tracking-wider font-semibold text-stone-500 bg-stone-100 border border-stone-200 rounded px-1.5 py-0.5" data-testid="payment-history-voided-badge">
                        Voided
                      </span>
                    )}
                    {e.mode && !e.voided && (
                      <span className="text-[9px] uppercase tracking-wider text-stone-500 bg-stone-100 border border-stone-200 rounded px-1.5 py-0.5">
                        {e.mode}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5" data-testid="payment-history-ts">
                    {fmtDate(e.ts)}
                    {e.reference && (
                      <span className="ml-2 font-mono text-[11px] text-stone-600">
                        Ref: {e.reference}
                      </span>
                    )}
                  </div>
                  {e.notes && (
                    <div className="text-xs text-stone-600 italic mt-1 truncate" title={e.notes}>
                      "{e.notes}"
                    </div>
                  )}
                </div>

                <div className="text-right shrink-0">
                  <div
                    className={`font-mono text-base tabular-nums ${
                      e.voided
                        ? "line-through text-stone-400"
                        : e.type === "adjustment" && Number(e.amount_signed) >= 0
                          ? "text-amber-800"
                          : "text-emerald-800"
                    }`}
                    data-testid="payment-history-amount"
                  >
                    {e.type === "adjustment" && Number(e.amount_signed) >= 0 ? "+" : ""}
                    {fmtInr(e.amount)}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">
                    Balance after
                  </div>
                  <div
                    className={`font-mono text-xs tabular-nums ${
                      Number(e.balance_after) < 0 ? "text-rose-700" : "text-stone-700"
                    }`}
                    data-testid="payment-history-balance"
                  >
                    {fmtInr(e.balance_after)}
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <footer className="px-4 py-2.5 border-t border-stone-200 bg-stone-50 flex items-center justify-between text-xs">
            <span className="text-stone-500">
              {history.count} entr{history.count === 1 ? "y" : "ies"}
            </span>
            <span className="text-stone-500">
              Current balance:{" "}
              <span
                className={`font-mono font-semibold ${
                  Number(history.closing_balance) < 0 ? "text-rose-700" : "text-stone-800"
                }`}
                data-testid="payment-history-closing"
              >
                {fmtInr(history.closing_balance)}
              </span>
            </span>
          </footer>
        </section>
      )}

      {/* iter164 — "My Shoots / My Work" — read-only detailed view of the
          logged-in staff's assigned shoots, replacing the plain payment
          history table. Reuses the same /team/me/earnings payload (backend
          enforces auth-user → staff resolution, no query params). */}
      <section className="rounded-lg border border-stone-200 bg-white overflow-hidden" data-testid="my-shoots-section">
        <header className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-serif text-lg text-stone-800">
            My Shoots / My Work
            <span className="ml-2 text-xs text-stone-500">
              ({shootRows.length} of {shootCounts.total})
            </span>
          </h2>
          <div className="inline-flex rounded-md border border-stone-200 bg-white overflow-hidden text-xs" role="tablist">
            {[
              { k: "all", label: `All (${shootCounts.total})` },
              { k: "upcoming", label: `Upcoming (${shootCounts.upcoming})` },
              { k: "completed", label: `Completed (${shootCounts.completed})` },
            ].map((tab) => (
              <button
                key={tab.k}
                type="button"
                role="tab"
                aria-selected={shootStatusFilter === tab.k}
                onClick={() => setShootStatusFilter(tab.k)}
                className={
                  "px-3 py-1.5 font-semibold transition-colors " +
                  (shootStatusFilter === tab.k
                    ? "bg-stone-900 text-white"
                    : "text-stone-600 hover:bg-stone-100")
                }
                data-testid={`my-shoots-filter-${tab.k}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </header>

        {shootRows.length === 0 ? (
          <div className="px-4 py-10 text-center text-stone-500">
            No shoots{monthFilter ? " in this month" : ""}{shootStatusFilter !== "all" ? ` in "${shootStatusFilter}"` : ""} yet.
          </div>
        ) : (
          <ul className="divide-y divide-stone-100" data-testid="my-shoots-list">
            {shootRows.map((r, i) => {
              const todayIso = new Date().toISOString().slice(0, 10);
              const isUpcoming = !r.event_date || r.event_date >= todayIso;
              // iter177 — Use the backend-classified earning_state so
              // per-shoot totals never disagree with the summary cards.
              const state = r.earning_state
                || (r.payroll_frozen ? "earned" : (isUpcoming ? "scheduled" : "excluded"));
              const paid = state === "earned" ? r.amount : 0;
              const pending = 0;                       // never surface as pending
              const scheduled = state === "scheduled" ? r.amount : 0;
              const excluded = state === "excluded";
              return (
                <li key={i} className="p-4 hover:bg-stone-50 transition-colors" data-testid={`my-shoot-card-${i}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 text-xs text-stone-500 font-mono">
                        <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.8} />
                        {fmtDate(r.event_date)}
                        {isUpcoming ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-semibold">
                            Upcoming
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">
                            Completed
                          </span>
                        )}
                        {r.payroll_frozen && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-200 text-stone-700 text-[10px] font-semibold">
                            <Lock className="w-2.5 h-2.5" /> Locked · Booking Completed
                          </span>
                        )}
                        {excluded && r.booking_status && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-200 text-stone-600 text-[10px] font-semibold" data-testid={`my-shoot-excluded-${i}`}>
                            Not counted · {r.booking_status}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 font-serif text-lg text-stone-900" data-testid={`my-shoot-couple-${i}`}>
                        {r.client_name || "—"}
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 flex-wrap text-sm text-stone-600">
                        <span className="inline-flex items-center gap-1">
                          <Camera className="w-3.5 h-3.5" strokeWidth={1.8} />
                          {r.event || "—"}
                        </span>
                        {r.place && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" strokeWidth={1.8} />
                            {r.place}
                          </span>
                        )}
                        {r.job_role && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 text-[11px] font-semibold uppercase tracking-wider">
                            {r.job_role}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Rate</div>
                      <div className={`text-xl font-serif tabular-nums ${excluded ? "text-stone-400 line-through" : "text-stone-900"}`} data-testid={`my-shoot-rate-${i}`}>
                        {fmtInr(r.amount)}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-[11px] text-stone-500 justify-end">
                        {state === "earned" && (
                          <span className="text-emerald-700" data-testid={`my-shoot-paid-${i}`}>Earned {fmtInr(paid)}</span>
                        )}
                        {state === "scheduled" && (
                          <span className="text-sky-700" data-testid={`my-shoot-scheduled-${i}`}>Scheduled {fmtInr(scheduled)}</span>
                        )}
                        {state === "excluded" && (
                          <span className="text-stone-400" data-testid={`my-shoot-not-counted-${i}`}>Not counted toward earnings</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {(r.notes || (r.equipment && r.equipment.length > 0)) && (
                    <div className="mt-3 pl-6 border-l-2 border-stone-200 text-sm text-stone-700 space-y-1">
                      {r.notes && (
                        <div className="flex items-start gap-2" data-testid={`my-shoot-notes-${i}`}>
                          <ClipboardList className="w-3.5 h-3.5 mt-0.5 text-stone-400" strokeWidth={1.8} />
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">My Work</div>
                            <div className="whitespace-pre-line">{r.notes}</div>
                          </div>
                        </div>
                      )}
                      {r.equipment && r.equipment.length > 0 && (
                        <div className="flex items-start gap-2 pt-1">
                          <Camera className="w-3.5 h-3.5 mt-0.5 text-stone-400" strokeWidth={1.8} />
                          <div className="flex gap-1 flex-wrap">
                            {r.equipment.map((eq, j) => (
                              <span key={j} className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 text-[10px] font-mono">
                                {eq}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-stone-200 bg-white overflow-hidden">
        <header className="px-4 py-3 border-b border-stone-200 bg-stone-50">
          <h2 className="font-serif text-lg text-stone-800">
            Weekly Payroll History
            <span className="ml-2 text-xs text-stone-500">({weeklyRows.length} weeks)</span>
          </h2>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Week</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-left px-4 py-2">Paid On</th>
              </tr>
            </thead>
            <tbody data-testid="my-earnings-weekly-rows">
              {weeklyRows.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-stone-500">
                  No weekly payroll payments{monthFilter ? " in this month" : ""} yet.
                </td></tr>
              )}
              {weeklyRows.map((w, i) => (
                <tr key={i} className="border-t border-stone-100" data-testid={`my-earnings-week-row-${i}`}>
                  <td className="px-4 py-2 font-mono text-stone-700 whitespace-nowrap">
                    {fmtDate(w.week_start)} – {fmtDate(w.week_end)}
                  </td>
                  <td className="px-4 py-2 text-right font-medium tabular-nums">{fmtInr(w.amount)}</td>
                  <td className="px-4 py-2 text-stone-500">{fmtDate((w.paid_at || "").slice(0, 10))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <MyLeads />
    </div>
  );
}
